"use client";

import React, { useEffect, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { DashboardSidebar } from "@/components/dashboard/dashboard-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { AuthState } from "@/types/auth";
import posthog from "posthog-js";
import { DashboardHeader } from "../dashboard/dashboard-header";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

// Breadcrumb configuration based on routes
const getBreadcrumbConfig = (pathname: string) => {
  if (
    pathname === "/dashboard/tasks" ||
    pathname.startsWith("/dashboard/tasks/")
  )
    return { title: "Cloud tasks" };
  const config = {
    "/dashboard": {
      title: "Dashboard",
    },
    "/dashboard/billing": {
      title: "Billing",
    },
    "/dashboard/settings": {
      title: "Settings",
    },
    "/dashboard/snippets": {
      title: "Snippets",
    },
    "/dashboard/help": {
      title: "Help & Support",
    },
    "/dashboard/onboarding": {
      title: "Welcome to FillApp",
    },
  };

  return config[pathname as keyof typeof config] || config["/dashboard"];
};

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, loading, state } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const destination = `${pathname}${search ? `?${search}` : ""}`;
  const hasTrackedAppAccess = useRef(false);

  const breadcrumbConfig = getBreadcrumbConfig(pathname);

  useEffect(() => {
    // Only redirect if we're not loading and user is not authenticated
    if (!loading && !user && state !== AuthState.LOADING) {
      const redirectUrl = `/signin?redirect=${encodeURIComponent(destination)}`;
      router.push(redirectUrl);
    }
  }, [user, loading, state, router, destination]);

  /* Track app access once per session when user is authenticated and app loads */
  useEffect(() => {
    if (user && !loading && !hasTrackedAppAccess.current) {
      posthog.capture("app_access");
      hasTrackedAppAccess.current = true;
    }
  }, [user, loading]);

  // Show loading state while checking authentication
  if (loading || state === AuthState.LOADING) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render the app if user is not authenticated
  if (!user) {
    return null;
  }
  if (pathname === "/dashboard/tasks/task") {
    return (
      <main className="h-dvh min-h-0 overflow-hidden bg-background">
        {children}
      </main>
    );
  }
  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <SidebarProvider className="flex-1 flex overflow-hidden">
        <DashboardSidebar variant="inset" />
        <SidebarInset className="flex-1 flex flex-col overflow-hidden">
          <DashboardHeader title={breadcrumbConfig.title} />
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            <div className="max-w-app-container mx-auto flex flex-col gap-4 md:gap-6">
              {children}
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
